import React, { useCallback, useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import {
  Button,
  ButtonProps,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem
} from '@material-ui/core';
import {
  GetApp as GetAppIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import { TemplateType } from '../../template/Template.interface';
import DocumentApprovalExportDialog from '../dialogs/DocumentApprovalExportDialog';
import { parseFilePath } from '../Document.helpers';
import { useProxyUrl, useDownloadAsPdf } from '../Document.hook';
import { FileDto } from '../Document.interface';

export interface DocumentDownloadButtonProps {
  file?: Pick<FileDto, 'id' | 'version'>;
  forceDownload?: boolean;
  storageKey: string;
  title?: string;
  asStartIcon?: boolean;
  allowTemplateTypes?: TemplateType[];
  buttonProps?: ButtonProps;
}

const DocumentDownloadButton: React.FC<DocumentDownloadButtonProps> = (
  props: DocumentDownloadButtonProps
) => {
  const {
    file,
    forceDownload,
    storageKey,
    title,
    asStartIcon = false,
    allowTemplateTypes = [],
    buttonProps
  } = props;
  const [error, setError] = useState<FetchError | undefined>();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const fileName: string = title || 'download';
  const downloadUrl: URL = useProxyUrl(storageKey, undefined, true, fileName);
  const [{ isLoading: isConverting, error: convertError }, blob, getDocument] =
    useDownloadAsPdf(storageKey);

  const isDoc = ['.doc', '.docx'].some(extension => {
    return storageKey.toLowerCase().includes(extension);
  });
  const isLoading: boolean = isConverting || isDownloading;
  const enableApprovalExport = !!(
    file && allowTemplateTypes?.includes(TemplateType.DocumentApproval)
  );
  const enableMenu = !forceDownload && (isDoc || enableApprovalExport);

  // download as is
  // download blob from url first, then save the response blob
  const downloadOriginal = useCallback(async () => {
    try {
      setIsDownloading(true);
      const response: Response = await fetch(downloadUrl.toString());
      // handle error
      if (!response.ok) {
        const { headers, status, statusText } = response;
        const contentType: string = headers.get('content-type') || '';
        return setError(
          contentType.includes('application/json')
            ? await response.json()
            : { error: '', statusCode: status, message: statusText }
        );
      }
      // save blob
      const blob: Blob = await response.blob();
      const { ext } = parseFilePath(storageKey);
      saveAs(blob, fileName + ext);
    } catch (error) {
      setError({ error: '', statusCode: 500, message: error.message });
    } finally {
      setIsDownloading(false);
    }
  }, [downloadUrl, storageKey, fileName]);

  useEffect(() => {
    if (blob) {
      saveAs(blob, `${fileName}.pdf`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob]);

  useEffect(() => {
    setError(convertError);
  }, [convertError]);

  return (
    <>
      <Button
        color='primary'
        variant='contained'
        disabled={isLoading}
        {...{
          [asStartIcon ? 'startIcon' : 'endIcon']: isLoading ? (
            <CircularProgress color='primary' size={20} />
          ) : enableMenu ? (
            <ArrowDropDownIcon />
          ) : (
            <GetAppIcon />
          )
        }}
        {...buttonProps}
        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          if (enableMenu) {
            setMenuAnchorEl(event.currentTarget);
          } else {
            downloadOriginal();
          }
        }}
      >
        Download
      </Button>

      <Menu
        open={!!menuAnchorEl}
        id='file-export-menu'
        anchorEl={menuAnchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            downloadOriginal();
          }}
        >
          Download File
        </MenuItem>
        {isDoc && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              getDocument();
            }}
          >
            Convert to PDF
          </MenuItem>
        )}
        {enableApprovalExport && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              setOpenExportDialog(true);
            }}
          >
            <ListItemText
              style={{ margin: 0 }}
              primary='Export PDF'
              secondary='Document Approval'
            />
          </MenuItem>
        )}
      </Menu>

      <AppSnackbar severity='error' open={!!error}>
        {error?.message || 'An error occurred while downloading the document.'}
      </AppSnackbar>

      {file && (
        <DocumentApprovalExportDialog
          file={file}
          open={openExportDialog}
          onCancel={() => setOpenExportDialog(false)}
          onSuccess={blob => {
            setOpenExportDialog(false);
            saveAs(blob, `${fileName}.pdf`);
          }}
        />
      )}
    </>
  );
};

export default DocumentDownloadButton;
